import React, { useEffect, useRef, useState } from 'react'
import Dropzone from 'react-dropzone'
import papaParse from 'papaparse'
import { Button, BUTTON_THEME, CheckBox, Input, TextArea } from '@st-operations/uikit/src/v2'

import { resolveCsv } from '_services/api-integrations'
import { getTaxonomyAudiences } from '_services/taxonomies'

import { splitIds } from '../constants'
import ChoiceTiles from './choice-tiles'

const MODES = [
  { value: 'all', label: 'Every audience in the taxonomy' },
  { value: 'picker', label: 'Pick them from a list' },
  { value: 'csv', label: 'Upload a CSV of mapping IDs' },
  { value: 'paste', label: 'Paste a list of mapping IDs' },
]

// the ways in that hand the server mapping IDs to match
const MATCHED_MODES = ['csv', 'paste']

// With no taxonomy there is nothing to send every audience of or pick from,
// so these are the only ways in - and the IDs are matched across every
// taxonomy.
const NO_TAXONOMY_MODES = MODES.filter((m) => MATCHED_MODES.includes(m.value))

const modeOf = (selection) => {
  if (MATCHED_MODES.includes(selection.source)) return selection.source
  return selection.mode === 'ids' ? 'picker' : 'all'
}

// What the server made of a file or a pasted list: how many IDs, how many
// matched an audience here, and the ones that did not. The matched ones are
// listed, with their names, in the preview of the names section below.
//
// With no taxonomy (`inTaxonomy` false) the IDs are matched across every
// taxonomy, and an ID more than one audience has is left out rather than
// guessed at - those are listed with the audiences that have them.
const MatchResult = ({ result, mode, inTaxonomy }) => (
  <div className="mt-4 text-sm">
    <div>
      <strong>{result?.counts?.in_file}</strong> mapping IDs{' '}
      {mode === 'csv' ? 'in the file' : 'pasted'}
    </div>
    <div>
      <strong>{result?.counts?.matched}</strong> matched audiences{' '}
      {inTaxonomy ? 'in this taxonomy' : 'across every taxonomy'}
    </div>
    <div className={result?.counts?.unmatched ? 'text-red-600' : ''}>
      <strong>{result?.counts?.unmatched}</strong> did not match
    </div>
    <If condition={result?.counts?.ambiguous}>
      <div className="text-red-600">
        <strong>{result.counts.ambiguous}</strong> matched more than one audience
      </div>
    </If>

    <If condition={result?.unmatched?.length}>
      <div className="mt-2 text-xs text-gray-600">
        <div className="mb-1">
          {inTaxonomy
            ? 'Not found — a header row, a typo, or an ID from another taxonomy:'
            : 'Not found — a header row, a typo, or an ID no audience has under this mapping ID:'}
        </div>
        <div className="font-mono" style={{ maxHeight: 140, overflowY: 'auto' }}>
          <For each="miss" of={result.unmatched.slice(0, 200)} index="i">
            <div key={i}>{miss}</div>
          </For>
        </div>
      </div>
    </If>

    <If condition={result?.ambiguous?.length}>
      <div className="mt-2 text-xs text-gray-600">
        <div className="mb-1">
          Left out — more than one audience has these, so there is no telling which is meant. A push
          from the right taxonomy can send them:
        </div>
        <div style={{ maxHeight: 140, overflowY: 'auto' }}>
          <For each="item" of={result.ambiguous.slice(0, 200)} index="i">
            <div key={i}>
              <span className="font-mono">{item.mapping_id}</span>:{' '}
              {item.audiences.map((a) => `${a.name} (${a.taxonomy || 'no taxonomy'})`).join(', ')}
            </div>
          </For>
        </div>
      </div>
    </If>
  </div>
)

// Which audiences this push sends.
//
// "All" is resolved when the push runs rather than when it is saved, so a
// draft saved today picks up audiences added tomorrow.
//
// The CSV and the pasted list hold mapping IDs - the values in audience.ids -
// not audience _ids and not names. That is why the mapping ID has to be chosen
// first: it says which column to match against. Nothing is chosen until the
// counts have been shown.
//
// Switching to either of those starts from nothing chosen. Keeping the
// previous choice until a file or list was matched meant that pressing Run on
// the CSV option, before any file, pushed every audience in the taxonomy.
//
// With no taxonomy, only those two are offered. A list chosen some other way -
// the failures of an earlier push, to retry - shows as already chosen under
// the CSV option, ready to be replaced.
const Audiences = ({ taxonomy, mappingId, value, onChange, disabled }) => {
  const selection = value || { mode: 'all' }
  const modes = taxonomy ? MODES : NO_TAXONOMY_MODES

  const [mode, setMode] = useState(() => {
    const current = modeOf(selection)
    return modes.some((m) => m.value === current) ? current : modes[0].value
  })
  const [audiences, setAudiences] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [pasted, setPasted] = useState('')
  const [matching, setMatching] = useState(false)
  const [match, setMatch] = useState(null)
  const [matchError, setMatchError] = useState(null)

  // Bumped whenever an answer still on its way stops being wanted - the list
  // was edited, or the option changed - so a late answer cannot overwrite
  // what is on screen now.
  const latest = useRef(0)

  const chosen = selection.audience_ids || []
  const pastedCount = new Set(splitIds(pasted)).size

  useEffect(() => {
    if (mode !== 'picker' || !taxonomy?.slug) return undefined
    let cancelled = false
    setLoading(true)
    getTaxonomyAudiences(taxonomy.slug, { limit: 250, q: search || undefined })
      .then((data) => {
        if (!cancelled) setAudiences(data?.items || [])
      })
      .catch(() => {
        if (!cancelled) setAudiences([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [mode, taxonomy, search])

  const invalidate = () => {
    latest.current += 1
    setMatching(false)
    setMatch(null)
    setMatchError(null)
  }

  // A file or list is matched against one mapping ID's column. Change the
  // mapping ID and that match no longer means anything, so it is dropped and
  // has to be made again - a pasted list stays in the box for that.
  const matchedFor = useRef(mappingId)
  useEffect(() => {
    if (matchedFor.current === mappingId) return
    matchedFor.current = mappingId
    if (!MATCHED_MODES.includes(mode)) return
    invalidate()
    if (chosen.length) onChange({ mode: 'ids', audience_ids: [], source: mode })
  }, [mappingId])

  const pick = (mode_) => {
    invalidate()
    setMode(mode_)
    if (mode_ === 'all') onChange({ mode: 'all' })
    if (mode_ === 'picker') onChange({ mode: 'ids', audience_ids: chosen, source: 'picker' })
    if (MATCHED_MODES.includes(mode_)) onChange({ mode: 'ids', audience_ids: [], source: mode_ })
  }

  const toggle = (id) => {
    const next = chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id]
    onChange({ mode: 'ids', audience_ids: next, source: 'picker' })
  }

  // match mapping IDs against this taxonomy's audiences - or, with none, every
  // taxonomy's - and choose what matched
  const resolve = async (values, extra) => {
    invalidate()
    const ticket = latest.current
    setMatching(true)
    try {
      const result = await resolveCsv(taxonomy?._id, mappingId, values)
      if (ticket !== latest.current) return
      setMatch(result)
      onChange({ mode: 'ids', audience_ids: result?.audience_ids || [], ...extra })
    } catch (e) {
      if (ticket === latest.current) setMatchError(e?.message || 'could not match those IDs')
    } finally {
      if (ticket === latest.current) setMatching(false)
    }
  }

  const onDrop = (files) => {
    const file = files?.[0]
    if (!file) return
    invalidate()
    const ticket = latest.current

    // parsed here so the whole file never has to be uploaded just to be told
    // a header row did not match anything
    papaParse.parse(file, {
      skipEmptyLines: true,
      complete: (parsed) => {
        if (ticket !== latest.current) return
        const values = (parsed?.data || [])
          .map((row) => (Array.isArray(row) ? row[0] : row))
          .filter(Boolean)

        if (!values.length) {
          setMatchError('that file had no rows in it')
          return
        }
        resolve(values, { source: 'csv', csv_filename: file.name })
      },
    })
  }

  // What is chosen always matches the box: an edit after matching clears the
  // choice until Match is pressed again, so Run can never send an old list.
  const onPaste = (text) => {
    setPasted(text)
    invalidate()
    if (chosen.length) onChange({ mode: 'ids', audience_ids: [], source: 'paste' })
  }

  return (
    <div>
      <div className="mb-4">
        <ChoiceTiles
          name="audience-mode"
          value={mode}
          options={modes}
          onChange={pick}
          disabled={disabled}
        />
      </div>

      <Choose>
        <When condition={mode === 'all'}>
          <div className="text-sm text-gray-500">
            Only audiences that are approved or processed. Resolved when the push runs, so any added
            or approved before then are included.
          </div>
        </When>

        <When condition={mode === 'picker'}>
          <div>
            <Input
              name="search"
              label="Search"
              value={search}
              onChange={(val) => setSearch(val)}
              disabled={disabled}
            />
            <div className="text-sm text-gray-500 my-2">{chosen.length} selected</div>
            <If condition={loading}>
              <div className="text-sm text-gray-400">loading…</div>
            </If>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <For each="audience" of={audiences}>
                <label
                  key={audience._id}
                  className="flex items-center gap-2 py-1 cursor-pointer text-sm"
                >
                  <CheckBox
                    checked={chosen.includes(audience._id)}
                    onChange={() => toggle(audience._id)}
                    disabled={disabled}
                  />
                  <span>{audience.name}</span>
                </label>
              </For>
            </div>
          </div>
        </When>

        <When condition={!mappingId}>
          <div className="text-sm text-amber-700">
            Choose a mapping ID first — it decides which IDs the {mode === 'csv' ? 'file' : 'list'}{' '}
            is matched against.
          </div>
        </When>

        <Otherwise>
          <div>
            <Choose>
              <When condition={mode === 'csv'}>
                <Dropzone multiple={false} onDrop={onDrop} disabled={disabled}>
                  {({ getRootProps, getInputProps }) => (
                    <div
                      {...getRootProps()}
                      className="border border-dashed border-gray-400 rounded p-6 text-center text-sm text-gray-500 cursor-pointer"
                    >
                      <input {...getInputProps()} />
                      Drop a CSV of mapping IDs here, or click to choose one. One ID per row, first
                      column.
                    </div>
                  )}
                </Dropzone>
              </When>

              <Otherwise>
                <TextArea
                  name="pasted_ids"
                  label="Mapping IDs"
                  placeholder="One per line, or separated by tabs, commas or spaces"
                  value={pasted}
                  onChange={(e, name, text) => onPaste(text)}
                  disabled={disabled}
                />
                <div className="flex items-center gap-3">
                  <Button
                    theme={BUTTON_THEME.SECONDARY}
                    onClick={() => resolve(splitIds(pasted), { source: 'paste' })}
                    disabled={disabled || matching || !pastedCount}
                  >
                    {matching ? 'Matching…' : 'Match IDs'}
                  </Button>
                  <span className="text-sm text-gray-500">{pastedCount} IDs found</span>
                </div>
              </Otherwise>
            </Choose>

            <If condition={matchError}>
              <div className="text-red-600 text-sm mt-3">{matchError}</div>
            </If>

            <Choose>
              <When condition={match}>
                <MatchResult result={match} mode={mode} inTaxonomy={Boolean(taxonomy)} />
              </When>
              <When condition={chosen.length}>
                <div className="mt-3 text-sm">
                  {chosen.length} audiences chosen
                  {selection.csv_filename ? ` from ${selection.csv_filename}` : ''}.{' '}
                  {mode === 'csv' ? 'Upload a file' : 'Paste a list'} to replace them.
                </div>
              </When>
            </Choose>
          </div>
        </Otherwise>
      </Choose>
    </div>
  )
}

export default Audiences
